var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { resolveTestId } from '@cvent/nucleus-test-automation';
import PropTypes from 'prop-types';
import { KEY_RETURN, KEY_SPACE } from '../utils/keyCodes';
import removeKeys from '../utils/removeKeys';
/**
A wrapper component to add "enter" keydown event and focusable state to non-interactive elements,
such as div, span, and li.

NOTE: When choosing interacive elements to use, button (or Button component from this library)
should be considered first, before landing on this component.
**/
export default class InteractiveElement extends React.Component {
    constructor(props) {
        super(props);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.focus = this.focus.bind(this);
    }
    focus() {
        if (this.element)
            this.element.focus();
    }
    handleKeyDown(e) {
        if ((e.keyCode === KEY_RETURN || e.keyCode === KEY_SPACE) && this.props.onClick) {
            this.props.onClick(e);
            if (e.keyCode === KEY_SPACE) {
                e.preventDefault();
            }
        }
    }
    render() {
        const _a = removeKeys(this.props, ['element']), { children, onKeyDown, role } = _a, rest = __rest(_a, ["children", "onKeyDown", "role"]);
        return React.createElement(this.props.element, Object.assign({}, resolveTestId(this.props), rest, { role: role, ref: c => this.element = c, onKeyDown: onKeyDown || this.handleKeyDown }), children);
    }
}
InteractiveElement.displayName = 'InteractiveElement';
InteractiveElement.propTypes = {
    /** Child nodes to render inside div. */
    children: PropTypes.node,
    /** Callback function to execute, when enter key is hit and onKeyDown prop isn't specified. */
    onClick: PropTypes.func,
    /** Callback function to execute for key down events. */
    onKeyDown: PropTypes.func,
    /**
    tabIndex="0" places element in the default tab navigation order;
    tabIndex="-1" removes element from the default order, but still allows it to receive focus programmatically.
    Default value is set to "0".
    **/
    tabIndex: PropTypes.oneOf(['0', '-1', 0, -1]),
    /** Non-interactive element, such as div, span, li. Default is set to "div" */
    element: PropTypes.string,
    /** Value for role attribute. Default to "button". */
    role: PropTypes.string
};
InteractiveElement.defaultProps = {
    tabIndex: '0',
    element: 'div',
    role: 'button'
};
